/**
 * Skills e MCPs (docs/refactor/14-skills-e-mcps.md), in two tabs.
 *
 * - **Skills**: on the desktop, the skills installed on this machine, each switched on or off with
 *   a box ([`MachineSkills`]); then the team's skill library — drop a SKILL.md to add one, or give a
 *   git URL and your own AI fetches, installs and saves it; install any skill on your agent with
 *   one click.
 * - **MCPs**: on the desktop, the MCP servers the agents started here get ([`McpServers`]).
 *
 * The team library is the same in the browser and in the desktop app: import and install are work
 * orders, so they reach whichever of the person's agents is listening. What is on this machine is
 * the desktop's to show; the browser says so (a per-machine listing on the web is still to come).
 */

import { useRef, useState, type DragEvent, type FormEvent } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Download, Eye, GitBranch, Loader2, Sparkles, Trash2, Upload } from 'lucide-react'
import { api, ApiError } from '../lib/api'
import { useI18n } from '../lib/i18n'
import { atLeast, getSession } from '../lib/auth'
import { readText } from '../lib/files'
import { isDesktop } from '../lib/desktop'
import { Badge, EmptyState, ErrorBanner, Segmented, Skeleton } from '../components/primitives'
import { Modal } from '../components/Modal'
import { MachineSkills } from '../components/MachineSkills'
import { RecommendedMcps, RecommendedSkills } from '../components/RecommendedCatalog'
import { McpServers } from '../components/McpServers'
import { MachinesInventory } from '../components/MachinesInventory'
import type { SkillSummary } from '../lib/types'

const KNOWN_ERRORS = ['invalid_skill', 'invalid_name', 'invalid_content', 'contains_secret', 'url_has_credentials', 'invalid_url', 'required', 'remote_off', 'too_many_pending']

function problemText(t: (key: string) => string, error: unknown): string {
  if (error instanceof ApiError && error.code && KNOWN_ERRORS.includes(error.code)) return t(`skills.error.${error.code}`)
  if (error instanceof Error && error.message === 'too-large') return t('skills.error.invalid_content')
  return t('error.generic')
}

type Tab = 'skills' | 'mcps'

export default function Skills() {
  const { t } = useI18n()
  const [params, setParams] = useSearchParams()
  const tab: Tab = params.get('tab') === 'mcps' ? 'mcps' : 'skills'
  const desktop = isDesktop()

  return (
    <div className="stack">
      <div className="skills-tabs">
        <Segmented<Tab>
          value={tab}
          onChange={(next) => setParams(next === 'skills' ? {} : { tab: next }, { replace: true })}
          options={[
            { value: 'skills', label: t('skillsPage.tab.skills') },
            { value: 'mcps', label: t('skillsPage.tab.mcps') },
          ]}
        />
      </div>
      {!desktop && <div className="notice small">{t(tab === 'mcps' ? 'skillsPage.webMcps' : 'skillsPage.webSkills')}</div>}
      {tab === 'skills' && (
        <>
          <RecommendedSkills />
          {desktop && <MachineSkills />}
          {/* Every computer of the person's, and installing on one of them from here (the web too). */}
          <MachinesInventory kind="skills" />
          <TeamLibrary />
        </>
      )}
      {tab === 'mcps' && (
        <>
          {desktop && <McpServers />}
          {!desktop && <RecommendedMcps />}
          <MachinesInventory kind="mcps" />
        </>
      )}
    </div>
  )
}

/** The team's skill library, as it has always been. */
function TeamLibrary() {
  const { t } = useI18n()
  const queryClient = useQueryClient()
  const canWrite = atLeast(getSession()?.role, 'Developer')
  const [dragging, setDragging] = useState(false)
  const [notes, setNotes] = useState<Array<{ file: string; ok: boolean; text: string }>>([])
  const [url, setUrl] = useState('')
  const [viewing, setViewing] = useState<string | null>(null)
  const [sent, setSent] = useState<string | null>(null)
  const [problem, setProblem] = useState<string | null>(null)
  const input = useRef<HTMLInputElement>(null)

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['skills'],
    queryFn: api.skills.list,
    // An import finishes when the agent saves the skill back.
    refetchInterval: (query) => (query.state.data?.some((s) => s.status === 'Importing') ? 5_000 : false),
  })
  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['skills'] })

  const upload = async (files: File[]) => {
    const results: typeof notes = []
    for (const file of files) {
      try {
        const skill = await api.skills.upload({ fileName: file.name, content: await readText(file) })
        results.push({ file: file.name, ok: true, text: t('skills.added', { name: skill.name }) })
      } catch (error) {
        results.push({ file: file.name, ok: false, text: problemText(t, error) })
      }
    }
    setNotes(results)
    refresh()
  }

  const onDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    setDragging(false)
    const files = [...event.dataTransfer.files].filter((f) => f.name.toLowerCase().endsWith('.md'))
    if (files.length === 0) {
      setNotes([{ file: '', ok: false, text: t('skills.onlyMd') }])
      return
    }
    void upload(files)
  }

  const importGit = useMutation({
    mutationFn: () => api.skills.importGit({ url: url.trim() }),
    onSuccess: () => {
      setUrl('')
      setSent(t('skills.importSent'))
      refresh()
    },
    onError: (error) => setProblem(problemText(t, error)),
  })

  const install = useMutation({
    mutationFn: (skill: SkillSummary) => api.skills.install(skill.id),
    onSuccess: (_, skill) => setSent(t('skills.installSent', { name: skill.name })),
    onError: (error) => setProblem(problemText(t, error)),
  })

  const remove = useMutation({
    mutationFn: (skill: SkillSummary) => api.skills.remove(skill.id),
    onSuccess: refresh,
  })

  const submitGit = (event: FormEvent) => {
    event.preventDefault()
    setProblem(null)
    setSent(null)
    if (!url.trim()) return
    importGit.mutate()
  }

  return (
    <div className="stack">
      <h2 className="panel__title" style={{ margin: 0 }}>{t('skillsPage.teamTitle')}</h2>
      <p className="muted small" style={{ maxWidth: 760, margin: 0 }}>{t('skills.intro')}</p>

      {canWrite && (
        <div className="skills-add">
          <div
            className={`skills-drop panel${dragging ? ' skills-drop--over' : ''}`}
            onDragOver={(event) => {
              if (event.dataTransfer.types.includes('Files')) {
                event.preventDefault()
                setDragging(true)
              }
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={onDrop}
            onClick={() => input.current?.click()}
            role="button"
            tabIndex={0}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') input.current?.click()
            }}
          >
            <Upload size={22} aria-hidden />
            <strong className="small">{t('skills.dropTitle')}</strong>
            <span className="tiny muted">{t('skills.dropHint')}</span>
            <input
              ref={input}
              type="file"
              accept=".md,text/markdown"
              multiple
              hidden
              onChange={(event) => {
                const files = [...(event.target.files ?? [])]
                event.target.value = ''
                if (files.length) void upload(files)
              }}
            />
          </div>

          <form className="panel stack skills-git" onSubmit={submitGit}>
            <strong className="small row row--tight"><GitBranch size={14} /> {t('skills.gitTitle')}</strong>
            <span className="tiny muted">{t('skills.gitHint')}</span>
            <div className="row row--tight">
              <input className="input mono" style={{ flex: 1, minWidth: 0 }} value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://github.com/anthropics/skills" aria-label={t('skills.gitTitle')} />
              <button type="submit" className="btn btn--primary" disabled={importGit.isPending || !url.trim()}>
                <Download size={14} /> {t('skills.importGit')}
              </button>
            </div>
          </form>
        </div>
      )}

      {notes.length > 0 && (
        <ul className="skills-notes small">
          {notes.map((note, i) => (
            <li key={i} className={note.ok ? 'skills-notes__ok' : 'skills-notes__bad'}>
              {note.file && <span className="mono">{note.file}: </span>}
              {note.text}
            </li>
          ))}
        </ul>
      )}
      {sent && (
        <div className="notice small">
          {sent} <Link to="/agents/work">{t('skills.followWork')}</Link>
        </div>
      )}
      {problem && <ErrorBanner message={problem} />}

      {error && <ErrorBanner message={t('error.generic')} onRetry={() => void refetch()} retryLabel={t('common.retry')} />}
      {isLoading && <Skeleton height={120} />}
      {data && data.length === 0 && <EmptyState title={t('skills.emptyTitle')} body={t('skills.emptyBody')} />}

      {data && data.length > 0 && (
        <div className="repo-list">
          {data.map((skill) => (
            <div key={skill.id} className="repo-row panel">
              {skill.status === 'Importing' ? <Loader2 size={18} className="repo-row__icon spin" /> : <Sparkles size={18} className="repo-row__icon" />}
              <div className="stack" style={{ gap: 2, minWidth: 0, flex: 1 }}>
                <div className="row row--tight row--wrap">
                  <strong className="mono">{skill.status === 'Importing' ? t('skills.importing') : skill.name}</strong>
                  {skill.status === 'Importing' && <Badge tone="accent">{t('skills.importingBadge')}</Badge>}
                </div>
                <span className="small">{skill.description}</span>
                {skill.sourceUrl && <span className="tiny muted mono truncate" title={skill.sourceUrl}>{skill.sourceUrl}</span>}
              </div>
              {skill.status === 'Ready' && (
                <>
                  <button type="button" className="btn btn--sm btn--ghost" onClick={() => setViewing(skill.id)}>
                    <Eye size={13} /> {t('skills.view')}
                  </button>
                  {canWrite && (
                    <button type="button" className="btn btn--sm" onClick={() => install.mutate(skill)} disabled={install.isPending}>
                      <Download size={13} /> {t('skills.install')}
                    </button>
                  )}
                </>
              )}
              {canWrite && (
                <button
                  type="button"
                  className="btn btn--sm btn--ghost btn--icon"
                  aria-label={t('skills.delete')}
                  title={t('skills.delete')}
                  onClick={() => {
                    if (window.confirm(t('skills.deleteConfirm', { name: skill.name }))) remove.mutate(skill)
                  }}
                >
                  <Trash2 size={13} />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {viewing && <SkillViewer id={viewing} onClose={() => setViewing(null)} />}
    </div>
  )
}

function SkillViewer({ id, onClose }: { id: string; onClose: () => void }) {
  const { t } = useI18n()
  const { data } = useQuery({ queryKey: ['skill', id], queryFn: () => api.skills.get(id) })
  return (
    <Modal title={data?.name ?? t('skills.view')} onClose={onClose} wide>
      {data ? <pre className="runner-log mono small skills-content">{data.content}</pre> : <Skeleton height={200} />}
    </Modal>
  )
}
